import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { OrderResponse } from '../../../../../../../../../../@shared/models/response.module';
import { PaymentService } from '../../../../../../../../../../@shared/services/payment.service';
import { ToastService } from '../../../../../../../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-refund-order-modal',
  standalone: false,
  templateUrl: './refund-order-modal.html',
  styleUrl: './refund-order-modal.css'
})
export class RefundOrderModal implements OnInit {
  @Input() order: OrderResponse;

  refundType: 'full' | 'partial' = 'full';
  refundAmountCents: number = 0;
  refundReason: string = '';
  processing: boolean = false;

  constructor(
    private activeModal: NgbActiveModal,
    private paymentService: PaymentService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    // Set default refund amount to remaining refundable amount
    this.refundAmountCents = this.getRemainingRefundable();
  }

  close() {
    this.activeModal.dismiss();
  }

  getRemainingRefundable(): number {
    return this.order.total_cents - (this.order.total_refunded_cents || 0);
  }

  getTotalRefunded(): number {
    return this.order.total_refunded_cents || 0;
  }

  hasPartialRefunds(): boolean {
    return (this.order.total_refunded_cents || 0) > 0;
  }

  formatCents(cents: number): string {
    return (cents / 100).toFixed(2);
  }

  formatCentsInput(cents: number): number {
    return cents / 100;
  }

  onAmountChange(value: number) {
    this.refundAmountCents = Math.round(value * 100);
  }

  onRefundTypeChange() {
    if (this.refundType === 'full') {
      this.refundAmountCents = this.getRemainingRefundable();
    } else {
      // Default partial to 50% of remaining
      this.refundAmountCents = Math.round(this.getRemainingRefundable() / 2);
    }
  }

  isAmountValid(): boolean {
    if (this.refundType === 'full') return true;
    return this.refundAmountCents > 0 && this.refundAmountCents <= this.getRemainingRefundable();
  }

  canSubmit(): boolean {
    return !this.processing && this.isAmountValid() && this.getRemainingRefundable() > 0;
  }

  formatDateTime(dateStr: string | null | undefined): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  submitRefund() {
    if (!this.canSubmit()) return;

    this.processing = true;

    const params = {
      amount_cents: this.refundType === 'full' ? undefined : this.refundAmountCents,
      reason: this.refundReason || undefined
    };

    this.paymentService.refundOrder(this.order.order_id, params).subscribe({
      next: (refund) => {
        this.processing = false;
        this.toastService.success(this.translateService.instant('manageEvent.orders.refundSuccess'));
        this.activeModal.close(refund);
      },
      error: (err) => {
        this.processing = false;
        console.error('Refund error:', err);
      }
    });
  }
}
